import API from 'utils/api';
import { onSuccess, onError } from 'utils/handlers';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import { aplicacionesCargosEnum } from 'views/Tramites/GPM/enums';

export const baseURI = '/recaudacion/cargos';

/**
 * GET Cargos
 * @returns {Promise<Array<object>>}
 */
export const getCargos = async (params = null) => {
  try {
    const response = await API.get(baseURI, { params });
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Cargos
 * @returns {Promise<Array<object>>}
 */
export const getCargosPaginados = async (params = null, all = true) => {
  try {
    const response = await API.get(`${baseURI}-paginados`, { params });
    if (all) {
      if (!response.data.next) return response.data.results;
      const page = new URL(response.data.next).searchParams.get('page');
      const _cargos = await getCargosPaginados({ ...params, page }, all);
      return response.data.results.concat(_cargos);
    }
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return all ? [] : { results: [] };
  }
};

/**
 * GET Cargo
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getCargo = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}`);
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Cargo
  * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postCargo = async (values) => {
  try {
    const formattedValues = getFormattedValues(values);
    const response = await API.post(baseURI, formattedValues);
    onSuccess(response, 'Agregado correctamente');
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * @description Determina el accionInf a registrar en bitácora según el motivo de cancelación
 * @param {boolean} prescripto
 * @param {string|null} motivoClave - clave del motivo de cancelación seleccionado
 * @returns {string}
 */
const getAccionInfCancelacion = (prescripto, motivoClave) => {
  if (prescripto) return 'CARGO_PRESCRIPTO';
  if (motivoClave === 'EXENTO') return 'CARGO_EXENTO';
  return 'CANCELAR_CARGO';
};

/**
 * PATCH Cargo
 * @param {number} id
 * @param {object} values
 * @param {boolean} feedback
 * @param {string|null} clave
 * @param {string|null} motivoClave - clave del motivo de cancelación seleccionado
 * @returns {Promise<object|boolean>}
 */
export const patchCargo = async (id, values, feedback = true, clave = null, motivoClave = null) => {
  try {
    const formattedValues = getFormattedValues(values);
    if (values.tipo_de_aplicacion === aplicacionesCargosEnum.CAPTURABLE) {
      formattedValues.importe_de_moneda = formattedValues.importe;
    }
    const config = values.cancelado
      ? {
        accionInf: getAccionInfCancelacion(values.prescripto, motivoClave),
        clave,
      }
      : {};
    const response = await API.patch(`${baseURI}/${id}`, formattedValues, config);
    if (feedback) {
      onSuccess(response, 'Editado correctamente');
    }
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Cargo
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const deleteCargo = async (id) => {
  try {
    const response = await API.delete(`${baseURI}/${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Recalcular Cargo
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postRecalcularCargo = async (id, values) => {
  try {
    const response = await API.post(`${baseURI}/${id}/recalcular/`, values);
    onSuccess(response, 'Recalculado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Recalcular Cargo
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postReactivarCargos = async (values) => {
  try {
    const response = await API.post(`${baseURI}/reactivar/`, values);
    onSuccess(response, 'Reactivados correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};
